<script>
    import {
        CardLayout,
        UnsetPatientBadDebtorByMedicareModal,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import {
        getFormatBirthDate,
        getFormatDate,
        getGenderType,
        getInsuranceType,
        getText,
        getTitle,
        goToURL,
        scrollIntoView,
    } from "../../../Utils/utilFunctions.svelte";

    export let patientDetail = {};
    export let loadData = () => {};

    let updatePatientBadDebtorByMedicareModalOpen = false,
        isUpdatingPatientBadDebtorByMedicare = false;

    const bottomLinks = [
        {
            displayName: "View Patient Encounters",
            clickFunc: () => _redirectToPatientEncounterTable(),
        },
        {
            displayName: "View Patient Fee Estimates",
            clickFunc: () => _redirectToPatientQuoteTable(),
        },
    ];

    let updatePatientBadDebtorByMedicare = async () => {
        isUpdatingPatientBadDebtorByMedicare = true;

        await apiMethods.putMethod
            .updatePatient({
                ...patientDetail,
                badDebtorByMedicare: false,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to unset patient Bad Debtor recognised by Medicare Number, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    _closeConfirmBadDebtorByMedicareModal();
                    loadData();
                }
            });

        isUpdatingPatientBadDebtorByMedicare = false;
    };

    // HELPER FUNCTIONS
    const _redirectToPatientEncounterTable = () => {
        const { firstName, middleName, lastName, id } = patientDetail;
        const searchName = `${firstName} ${middleName} ${lastName}`;

        goToURL(`/PatientEncounter/page=0&size=25&patientId=${id}`);
    };

    const _redirectToPatientQuoteTable = () => {
        const { firstName, middleName, lastName } = patientDetail;
        const searchName = `${firstName} ${middleName} ${lastName}`;

        goToURL(
            `/PatientQuote/page=0&size=25&search=${searchName.replaceAll(
                " ",
                "%20",
            )}`,
        );
    };

    const _getPatientFullName = () => {
        const { title, firstName, middleName, lastName } = patientDetail;

        return `${getTitle(title)} ${firstName} ${middleName} ${lastName}`;
    };

    const _getPatientAddress = () => {
        const { address, city, state, postCode } = patientDetail;

        const res = [];

        if (address) res.push(address);
        if (city) res.push(city);
        if (state) res.push(state);
        if (postCode) res.push(postCode);

        return res.join(", ");
    };

    const _openConfirmChangeBadDebtorByMedicareModal = () =>
        (updatePatientBadDebtorByMedicareModalOpen = true);

    const _closeConfirmBadDebtorByMedicareModal = () =>
        (updatePatientBadDebtorByMedicareModalOpen = false);

    $: hasDefaultContact = checkPatientContacts(patientDetail);

    let defaultContact;

    const checkPatientContacts = (data) => {
        if (data && data.patientContacts && data.patientContacts.length) {
            defaultContact = data.patientContacts.find((c) => c.useAsContact);
            return !!defaultContact;
        } else {
            defaultContact = null;
            return false;
        }
    }
</script>

<CardLayout title="Patient Details" {bottomLinks}>
    <div class="flex flex-col flex-1 px-2 py-4 w-full lg:flex-row">
        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Full Name:</p>
                <p class="content">
                    {_getPatientFullName()}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Gender:</p>
                <p class="content">{getGenderType(patientDetail.sex)}</p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Date of Birth:</p>
                <p class="content">
                    {getFormatBirthDate(patientDetail.birthDate)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Phone:</p>
                <p class="content">{getText(patientDetail.phone)}</p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Email:</p>
                {#if patientDetail.email}
                    <a
                        href="mailto:{patientDetail.email}"
                        target="_blank"
                        class="content break-all hover:text-[#8A3ED7] hover:underline hover:font-bold"
                    >
                        {getText(patientDetail.email)}
                    </a>
                {/if}
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">UR Number:</p>
                <p class="content">{getText(patientDetail.urNumber)}</p>
            </div>

            {#if hasDefaultContact}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Contact Person:</p>
                    <p
                        on:click={() => scrollIntoView("contactsTable")}
                        class="content plink"
                    >
                        {defaultContact.name}
                    </p>
                </div>
            {/if}

            <div class="flex flex-row gap-x-2">
                <p class="title">Address:</p>
                <p class="content">{_getPatientAddress()}</p>
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            {#if patientDetail.badDebtor || patientDetail.badDebtorByMedicare}
                <div class="cursor-default badge badge-error">
                    {patientDetail.badDebtorByMedicare
                        ? "Bad Debtor recognised by Medicare Number"
                        : "Bad Debtor"}
                </div>
            {/if}

            <!-- {#if patientDetail.badDebtorByMedicare}
                <div
                    class="badge badge-error cursor-pointer"
                    on:click={_openConfirmChangeBadDebtorByMedicareModal}
                >
                    Bad Debtor recognised by Medicare Number
                </div>
            {/if} -->

            <div class="flex flex-row gap-x-2">
                <p class="title">Insurance Type:</p>
                <p class="content">
                    {getInsuranceType(patientDetail.insuranceType)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Medicare No.:</p>
                <p class="content">{getText(patientDetail.medicareNo)}</p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Medicare Ref. No.:</p>
                <p class="content">
                    {getText(patientDetail.medicareRefNo)}
                </p>
            </div>
            {#if patientDetail.veteranNo}
                <div class="flex flex-row gap-x-2">
                    <p class="title">Veteran Number:</p>
                    <p class="content">
                        {getText(patientDetail.veteranNo)}
                    </p>
                </div>
            {/if}
            <div class="flex flex-row gap-x-2">
                <p class="title">Insurance:</p>
                <p class="content">
                    {getText(patientDetail.insuranceFundName)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Insurance Details:</p>
                <p class="content">
                    {getText(patientDetail.insurance)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Insurance Membership No.:</p>
                <p class="content">
                    {getText(patientDetail.membershipNo)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Insurance Membership Ref. No.:</p>
                <p class="content">
                    {getText(patientDetail.membershipRefNo)}
                </p>
            </div>
        </div>

        <div class="divider lg:divider-horizontal" />

        <div class="flex flex-col flex-1 gap-y-4 h-full">
            <div class="flex flex-row gap-x-2">
                <p class="title">Created Date:</p>
                <p class="content">
                    {getFormatDate(patientDetail.createdDate)}
                </p>
            </div>

            <div class="flex flex-row gap-x-2">
                <p class="title">Last Update Date:</p>
                <p class="content">
                    {getFormatDate(patientDetail.updatedDate)}
                </p>
            </div>
        </div>
    </div>

    <!-- <div class="flex flex-col flex-1 px-2 mt-4 gap-2">
        <p class="title font-bold underline">Patient Notes:</p>
        <div
            class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8"
        >
            {getText(patientDetail.notes ? patientDetail.notes : "-")}
        </div>
    </div>

    <div class="flex flex-col flex-1 px-2 mt-4 gap-2">
        <p class="title text-error font-bold underline">Patient Alerts:</p>
        <div
            class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8"
        >
            {getText(patientDetail.alerts ? patientDetail.alerts : "-")}
        </div>
    </div> -->
</CardLayout>

<UnsetPatientBadDebtorByMedicareModal
    bind:isModalOpen={updatePatientBadDebtorByMedicareModalOpen}
    isConfirming={isUpdatingPatientBadDebtorByMedicare}
    closeModal={_closeConfirmBadDebtorByMedicareModal}
    confirmAction={updatePatientBadDebtorByMedicare}
/>

<style>
    .title {
        width: 200px;
    }

    .content {
        width: calc(100% - 200px);
    }
</style>
